import { AccessLevel, AccessMatrixRow, CatalogPosition, Role, UserAccount, UserStatus } from '../../models';
import { PAGE_TABS } from '../../util/access-control.util';
import { ROLES } from '../prototype/access-data';
import { EMPLOYEE_PHOTOS, USERS } from '../prototype/company-directory-data';
import { POSITION_CATALOG } from '../prototype/os-data';

const photos = EMPLOYEE_PHOTOS as Record<string, string>;

/** User accounts — mapped from the prototype USERS. */
export const USERS_SEED: readonly UserAccount[] = (USERS as any[]).map(
  (u): UserAccount => ({
    id: u.id,
    name: u.name ?? '',
    email: u.email ?? '',
    phone: u.phone ?? '',
    personaId: u.personaId ?? null,
    positionId: u.positionId ?? null,
    role: u.role ?? 'employee',
    status: (u.status as UserStatus) ?? 'active',
    lastLogin: u.lastLogin ?? '—',
    profilePic: u.profilePic ?? '',
    fallbackPhoto: photos[u.id] ?? '',
  }),
);

/** Role definitions — mapped from the prototype ROLES. */
export const ROLES_SEED: readonly Role[] = (ROLES as any[]).map(
  (r): Role => ({ id: r.id, label: r.label ?? '', desc: r.desc ?? '' }),
);

/**
 * Access matrix — one row per **sidebar item**, so the matrix mirrors the nav
 * exactly (same sections, same order). Levels keyed by role id. Level presets:
 * admin always edit-all; personal screens = edit-own; company screens = view;
 * management screens (projects/team) = managers only; admin screens = admin only.
 */
type Levels = Record<'admin' | 'ceo' | 'supervisor' | 'employee' | 'trade', AccessLevel>;
const ADMIN_ONLY: Levels = { admin: 'edit-all', ceo: 'none', supervisor: 'none', employee: 'none', trade: 'none' };
const EVERYONE_VIEW: Levels = { admin: 'edit-all', ceo: 'view', supervisor: 'view', employee: 'view', trade: 'view' };
const PERSONAL: Levels = { admin: 'edit-all', ceo: 'edit-own', supervisor: 'edit-own', employee: 'edit-own', trade: 'edit-own' };
const MANAGERS: Levels = { admin: 'edit-all', ceo: 'view', supervisor: 'view', employee: 'none', trade: 'none' };
const TEAM: Levels = { admin: 'edit-all', ceo: 'edit-team', supervisor: 'edit-team', employee: 'none', trade: 'none' };

const row = (section: string, resource: string, levels: Levels): AccessMatrixRow => ({
  section,
  resource,
  levels: { ...levels },
});

/**
 * One row per TAB of a resource, so every tab is gated in its own right rather
 * than inheriting its page. Inheritance is still the fallback for a tab nobody has
 * set, but a tab with no row cannot be given a level of its own — which is the
 * whole point of the grid.
 *
 * Generated from {@link PAGE_TABS} rather than listed again here. That constant is
 * already the single source of which tabs a page has, and typing them twice is how
 * the two drift: the matrix carried an Onboarding `stages` tab the page had stopped
 * rendering, and nobody noticed because nothing compared them.
 *
 * Each tab starts at its page's level, so seeding these changes nothing about who
 * sees what today — it only makes each one settable. `overrides` is for the tabs
 * that must start somewhere else.
 */
const tabRows = (
  section: string,
  resource: string,
  levels: Levels,
  overrides: Record<string, Levels> = {},
): AccessMatrixRow[] =>
  (PAGE_TABS[resource] ?? []).map((t) => ({
    section,
    resource,
    tab: t.key,
    levels: { ...(overrides[t.key] ?? levels) },
  }));

export const ACCESS_MATRIX_SEED: readonly AccessMatrixRow[] = [
  // ── Home ──
  row('Home', 'Get Started', ADMIN_ONLY),
  row('Home', 'Today', PERSONAL),
  row('Home', 'Command Center', ADMIN_ONLY),
  // ── Me ──
  row('Me', 'Onboarding', PERSONAL),
  row('Me', 'My Packet', PERSONAL),
  ...tabRows('Me', 'My Packet', PERSONAL),
  row('Me', 'RPM Plan', PERSONAL),
  row('Me', 'Checklists', PERSONAL),
  row('Me', 'Weekly Schedule', PERSONAL),
  row('Me', 'MRP', PERSONAL),
  ...tabRows('Me', 'MRP', PERSONAL),
  row('Me', 'My Career Pathway', EVERYONE_VIEW),
  row('Me', 'My Training', EVERYONE_VIEW),
  ...tabRows('Me', 'My Training', EVERYONE_VIEW),
  row('Me', 'My Projects', PERSONAL),
  ...tabRows('Me', 'My Projects', PERSONAL),
  row('Me', 'Time Tracking', PERSONAL),
  ...tabRows('Me', 'Time Tracking', PERSONAL),
  row('Me', 'My Team', TEAM),
  // ── Projects ──
  row('Projects', 'Opportunities', MANAGERS),
  ...tabRows('Projects', 'Opportunities', MANAGERS),
  row('Projects', 'Scheduling', MANAGERS),
  ...tabRows('Projects', 'Scheduling', MANAGERS),
  row('Projects', 'Project Management', MANAGERS),
  ...tabRows('Projects', 'Project Management', MANAGERS),
  row('Projects', 'Financials', MANAGERS),
  ...tabRows('Projects', 'Financials', MANAGERS),
  row('Projects', 'Project Settings', MANAGERS),
  ...tabRows('Projects', 'Project Settings', MANAGERS),
  // ── Diamond Ridge ──
  row('Diamond Ridge', 'Company', EVERYONE_VIEW),
  ...tabRows('Diamond Ridge', 'Company', EVERYONE_VIEW),
  row('Diamond Ridge', 'Business Model', ADMIN_ONLY),
  ...tabRows('Diamond Ridge', 'Business Model', ADMIN_ONLY),
  row('Diamond Ridge', 'Org Chart', EVERYONE_VIEW),
  row('Diamond Ridge', 'People', EVERYONE_VIEW),
  // The People > Onboarding tab EMBEDS the admin onboarding area, so it is gated
  // apart from the everyone-view team directory: employees and trade do not see
  // it. The tabs INSIDE it are the Onboarding Process rows below — the same
  // component, gated by the same resource wherever it is rendered — so all three
  // levels are settable: the page, this tab, and each tab within it.
  ...tabRows('Diamond Ridge', 'People', EVERYONE_VIEW, { onboarding: ADMIN_ONLY }),
  // Onboarding Process sits HERE, with People, because that is where it is
  // reached: it has no sidebar item of its own and is rendered inside the
  // People > Onboarding tab. Listing it at the end of the section implied a page
  // nobody can navigate to. Its four rows are the tabs INSIDE that tab, so People
  // is gated at all three levels — the page, the Onboarding tab, and each tab
  // within it.
  row('Diamond Ridge', 'Onboarding Process', ADMIN_ONLY),
  ...tabRows('Diamond Ridge', 'Onboarding Process', ADMIN_ONLY),
  row('Diamond Ridge', 'Operating Systems', EVERYONE_VIEW),
  row('Diamond Ridge', 'Career Pathways', ADMIN_ONLY),
  row('Diamond Ridge', 'Training Modules', EVERYONE_VIEW),
  ...tabRows('Diamond Ridge', 'Training Modules', EVERYONE_VIEW),
  row('Diamond Ridge', 'Handbook', EVERYONE_VIEW),
  // ── Admin ──
  row('Admin', 'Users & Access', ADMIN_ONLY),
  ...tabRows('Admin', 'Users & Access', ADMIN_ONLY),
  // In the sidebar and, until now, in no row — so `resourceForRoute` found nothing,
  // access failed open, and the one screen nobody could hide was the changelog.
  // Everyone-view keeps that exactly as it behaved; it is now a decision rather
  // than an omission.
  row('Admin', 'Release Notes', EVERYONE_VIEW),
];

/** Catalog positions — minimal shape for the position-assignment dropdown. */
export const POSITION_CATALOG_SEED: readonly CatalogPosition[] = (POSITION_CATALOG as any[]).map(
  (p): CatalogPosition => {
    const tier = p.tier ?? '';
    const pathwayId = p.pathwayId ?? '';
    return {
      id: p.id,
      title: p.title ?? '',
      tier,
      pathwayId,
      // Seed the memberships list from the prototype's single pathway/tier; the
      // Career Pathways screen can then attach the same seat to further pathways.
      pathways: pathwayId ? [{ pathwayId, tier }] : [],
      // Division this seat belongs to — powers the person form's
      // division-first position cascade.
      division: p.division ?? '',
    };
  },
);
